import React, { useState, useEffect } from "react";
import ReactSelect from "react-select";
import { FiCalendar, FiFileText, FiDownload, FiFilter } from "react-icons/fi";
import { facturasCliente } from "../api/facturar";
import { reporteGeneral } from "../api/reportes";
import { showAlertExito, showAlertError, showAlertInfo } from "../utils/alertHelper";
import Loader from "./Loader";

const ReporteGeneral = () => {
    const [loading, setLoading] = useState(false);
    const [fetchingTypes, setFetchingTypes] = useState(true);
    const [dteTypes, setDteTypes] = useState([]);
    const [selectedDteTypes, setSelectedDteTypes] = useState([]);
    const [reportData, setReportData] = useState([]);

    // Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Fechas por defecto
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const lastDay = today.toISOString().split('T')[0];

    const [fechaInicio, setFechaInicio] = useState(firstDay);
    const [fechaFin, setFechaFin] = useState(lastDay);

    useEffect(() => {
        const fetchDteTypes = async () => {
            try {
                const response = await facturasCliente();
                if (response && response.data) {
                    const options = response.data.map(item => {
                        const doc = item.typeFact || item;
                        return {
                            value: doc.codigo,
                            label: `${doc.codigo} | ${doc.name}`,
                            name: doc.name // Guardamos el nombre para el reporte
                        };
                    });
                    setDteTypes(options);
                    // Seleccionar todos por defecto si se desea, o dejar vacio
                    // setSelectedDteTypes(options);
                }
            } catch (error) {
                console.error("Error fetching DTE types:", error);
            } finally {
                setFetchingTypes(false);
            }
        };

        fetchDteTypes();
    }, []);

    const handleGenerarReporte = async (e) => {
        e.preventDefault();

        if (selectedDteTypes.length === 0) {
            showAlertError("Campo requerido", "Debe seleccionar al menos un tipo de dte.");
            return;
        }

        setLoading(true);
        setReportData([]);
        setCurrentPage(1); // Reset a la primera página
        try {
            const payload = {
                fechaInicio,
                fechaFin,
                dteTypeReport: selectedDteTypes.map(t => t.value)
            };

            const response = await reporteGeneral(payload);

            // Aquí se manejaría la respuesta, por ejemplo descargar un PDF o Excel si el backend lo devuelve
            // O mostrar un mensaje de éxito si es un proceso asíncrono.
            if (response && response.data) {
                setReportData(response.data);
                showAlertExito("Éxito", "Reporte generado correctamente.");
            } else {
                showAlertInfo("Sin datos", "No se encontraron ventas para los filtros seleccionados.");
            }
            console.log("Reporte generado:", response);

        } catch (error) {
            console.error("Error generando reporte:", error);
        } finally {
            setLoading(false);
        }
    };

    // Función para exportar a Excel (CSV)
    const exportToExcel = () => {
        if (reportData.length === 0) return;

        const headers = ["Fecha", "Documento (Serial)", "Tipo de Factura", "Cod. Generación", "Total"];
        const rows = reportData.map(item => [
            item.fecha || (item.createdAt ? item.createdAt.split('T')[0] : "---"),
            item.dteSerial || "---",
            item.TipoDocumento?.name || item.typeFacts || "---",
            item.codGeneration || "---",
            Number(item.totalPagar || item.total || 0).toFixed(2)
        ]);

        const csvContent = "data:text/csv;charset=utf-8,\uFEFF"
            + headers.join(",") + "\n"
            + rows.map(e => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Reporte_Ventas_${fechaInicio}_al_${fechaFin}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Lógica de paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = reportData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(reportData.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="mb-8">
                <div className="flex justify-between items-start">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                            <FiFileText className="text-indigo-600" /> Generar Reporte General
                        </h2>
                        <p className="text-slate-500 mt-1">
                            Seleccione el rango de fechas y los tipos de documentos para el reporte consolidado.
                        </p>
                    </div>
                    {reportData.length > 0 && (
                        <button
                            onClick={exportToExcel}
                            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold transition-all shadow-md active:scale-95"
                        >
                            <FiDownload /> Exportar a Excel
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 relative">
                {loading && (
                    <div className="absolute inset-0 bg-white/70 flex justify-center items-center z-10 rounded-2xl">
                        <Loader />
                    </div>
                )}

                <form onSubmit={handleGenerarReporte} className="p-8 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Fecha Inicio */}
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-slate-700">
                                Fecha de Inicio
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiCalendar className="text-slate-400" />
                                </div>
                                <input
                                    type="date"
                                    value={fechaInicio}
                                    onChange={(e) => setFechaInicio(e.target.value)}
                                    className="block w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-slate-50 focus:bg-white text-slate-700"
                                    required
                                />
                            </div>
                        </div>

                        {/* Fecha Fin */}
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-slate-700">
                                Fecha de Fin
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiCalendar className="text-slate-400" />
                                </div>
                                <input
                                    type="date"
                                    value={fechaFin}
                                    onChange={(e) => setFechaFin(e.target.value)}
                                    className="block w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-slate-50 focus:bg-white text-slate-700"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Tipos de DTE */}
                    <div className="space-y-2">
                        <label className="block text-sm font-semibold text-slate-700 flex items-center gap-2">
                            <FiFilter className="text-slate-400" /> Tipos de Factura / DTE
                        </label>
                        <ReactSelect
                            isMulti
                            options={dteTypes}
                            value={selectedDteTypes}
                            onChange={setSelectedDteTypes}
                            placeholder="Seleccione uno o varios tipos..."
                            isLoading={fetchingTypes}
                            className="text-sm"
                            menuPortalTarget={document.body}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: '0.75rem',
                                    padding: '2px',
                                    borderColor: '#cbd5e1',
                                    '&:hover': { borderColor: '#6366f1' },
                                    boxShadow: 'none',
                                    backgroundColor: '#f8fafc'
                                }),
                                multiValue: (base) => ({
                                    ...base,
                                    backgroundColor: '#e0e7ff',
                                    borderRadius: '0.5rem',
                                    color: '#4338ca'
                                }),
                                multiValueLabel: (base) => ({
                                    ...base,
                                    color: '#4338ca',
                                    fontWeight: '500'
                                }),
                                multiValueRemove: (base) => ({
                                    ...base,
                                    color: '#4338ca',
                                    '&:hover': { backgroundColor: '#c7d2fe', color: '#3730a3' }
                                }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 })
                            }}
                        />
                        <p className="text-xs text-slate-400 italic mt-1">
                            * Puede seleccionar múltiples tipos de documentos para incluirlos en el reporte.
                        </p>
                    </div>

                    <div className="pt-4 flex justify-end">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all shadow-lg ${loading
                                ? 'bg-indigo-400 cursor-not-allowed'
                                : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200 active:scale-95'
                                }`}
                        >
                            <FiDownload /> {loading ? "Generando..." : "Generar Reporte"}
                        </button>
                    </div>
                </form>
            </div>

            {/* Tabla de Resultados */}
            {reportData.length > 0 && (
                <div className="mt-8 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden animate-fade-in">
                    <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                            <FiFileText className="text-indigo-600" /> Detalle de Ventas Generadas
                        </h3>
                        <span className="text-xs font-semibold px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full">
                            {reportData.length} registros encontrados
                        </span>
                    </div>
                    <div className="overflow-x-auto custom-scrollbar">
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-slate-500 bg-slate-50 uppercase border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-4 font-semibold">Fecha</th>
                                    <th className="px-6 py-4 font-semibold">Documento (Serial)</th>
                                    <th className="px-6 py-4 font-semibold">Tipo DTE</th>
                                    <th className="px-6 py-4 font-semibold text-center">Cód. Generación</th>
                                    <th className="px-6 py-4 font-semibold text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {currentItems.map((item, idx) => (
                                    <tr key={idx} className="hover:bg-indigo-50/30 transition-colors group">
                                        <td className="px-6 py-4 text-slate-600 whitespace-nowrap">
                                            {item.fecha || (item.createdAt ? item.createdAt.split('T')[0] : "---")}
                                        </td>
                                        <td className="px-6 py-4 text-slate-700 font-medium whitespace-nowrap">
                                            {item.dteSerial || "---"}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-[11px] font-bold border border-indigo-100">
                                                {item.TipoDocumento?.name || item.typeFacts || "N/A"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-slate-400 text-[11px] font-mono text-center">
                                            {item.codGeneration || "---"}
                                        </td>
                                        <td className="px-6 py-4 text-right text-indigo-600 font-bold">
                                            ${Number(item.totalPagar || item.total || 0).toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Controles de Paginación */}
                    {totalPages > 1 && (
                        <div className="p-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between">
                            <p className="text-xs text-slate-500">
                                Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, reportData.length)} de {reportData.length} registros
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currentPage === 1
                                        ? 'text-slate-300 cursor-not-allowed'
                                        : 'text-slate-600 hover:bg-white hover:shadow-sm border border-slate-200'
                                        }`}
                                >
                                    Anterior
                                </button>
                                {[...Array(totalPages)].map((_, i) => {
                                    const pageNum = i + 1;
                                    // Logic to show a limited number of page buttons
                                    if (totalPages <= 7 || 
                                        pageNum === 1 || 
                                        pageNum === totalPages || 
                                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                                        return (
                                            <button
                                                key={i}
                                                onClick={() => paginate(pageNum)}
                                                className={`w-8 h-8 rounded-lg text-sm font-bold transition-all ${currentPage === pageNum
                                                    ? 'bg-indigo-600 text-white shadow-indigo-100 shadow-md'
                                                    : 'text-slate-600 hover:bg-white border border-transparent hover:border-slate-200'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                        return <span key={i} className="px-1 text-slate-400">...</span>;
                                    }
                                    return null;
                                })}
                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currentPage === totalPages
                                        ? 'text-slate-300 cursor-not-allowed'
                                        : 'text-slate-600 hover:bg-white hover:shadow-sm border border-slate-200'
                                        }`}
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Ayuda / Info */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex gap-3">
                    <div className="bg-blue-100 p-2 rounded-lg h-fit text-blue-600">
                        <FiFileText size={20} />
                    </div>
                    <div>
                        <h4 className="font-bold text-blue-800 text-sm">Reporte Consolidado</h4>
                        <p className="text-blue-600 text-xs mt-1">
                            Este reporte incluye todas las transacciones realizadas en el periodo seleccionado para los tipos de DTE escogidos.
                        </p>
                    </div>
                </div>
                <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl flex gap-3">
                    <div className="bg-indigo-100 p-2 rounded-lg h-fit text-indigo-600">
                        <FiCalendar size={20} />
                    </div>
                    <div>
                        <h4 className="font-bold text-indigo-800 text-sm">Periodo de Tiempo</h4>
                        <p className="text-indigo-600 text-xs mt-1">
                            El sistema toma por defecto el primer día del mes actual para facilitar la generación mensual.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReporteGeneral;
